import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "../Button/Button";
import { Select } from "../Select/Select";
import { clsx } from "clsx";

const DEFAULT_ROW_LIMIT_OPTIONS = [5, 10, 25, 50, 100];

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    itemsPerPage?: number;
    totalItems?: number;
    showPageNumbers?: boolean;
    maxVisiblePages?: number;
    /** When true, shows a row limit selector to change items per page. Requires onItemsPerPageChange. */
    rowLimitEnabled?: boolean;
    /** Callback when user changes the row limit. Parent should update per_page and typically reset to page 1. */
    onItemsPerPageChange?: (value: number) => void;
    /** Options for the row limit selector. Defaults to [5, 10, 25, 50, 100]. */
    rowLimitOptions?: number[];
    className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage,
    totalItems,
    showPageNumbers = true,
    maxVisiblePages = 5,
    rowLimitEnabled = false,
    onItemsPerPageChange,
    rowLimitOptions = DEFAULT_ROW_LIMIT_OPTIONS,
    className,
}) => {
    const showRowLimit =
        rowLimitEnabled &&
        onItemsPerPageChange &&
        itemsPerPage != null &&
        (totalItems ?? 0) > 10;
    const showPagination = totalPages > 1 || showRowLimit;

    if (!showPagination) {
        return null;
    }

    const getVisiblePages = () => {
        const pages: (number | string)[] = [];
        const half = Math.floor(maxVisiblePages / 2);

        let start = Math.max(1, currentPage - half);
        let end = Math.min(totalPages, currentPage + half);

        if (currentPage <= half) {
            end = Math.min(maxVisiblePages, totalPages);
        }

        if (currentPage >= totalPages - half) {
            start = Math.max(1, totalPages - maxVisiblePages + 1);
        }

        if (start > 1) {
            pages.push(1);
            if (start > 2) {
                pages.push("...");
            }
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (end < totalPages) {
            if (end < totalPages - 1) {
                pages.push("...");
            }
            pages.push(totalPages);
        }

        return pages;
    };

    const visiblePages = getVisiblePages();
    const startItem = totalItems ? (currentPage - 1) * (itemsPerPage || 10) + 1 : undefined;
    const endItem = totalItems
        ? Math.min(currentPage * (itemsPerPage || 10), totalItems)
        : undefined;

    const currentLimit = itemsPerPage ?? 10;

    const handleLimitChange = (e: { target: { value: string } }) => {
        const value = parseInt(e.target.value, 10);
        if (!Number.isNaN(value)) {
            onItemsPerPageChange?.(value);
            onPageChange(1);
        }
    };

    return (
        <div className={clsx("flex flex-col sm:flex-row items-center justify-between gap-4 mt-4", className)}>
            <div className="flex flex-wrap items-center gap-4 flex-1">
                {totalItems && (
                    <div className="text-sm text-neutral-dark dark:text-neutral-light opacity-80">
                        Showing {startItem} to {endItem} of {totalItems} results
                    </div>
                )}
                {showRowLimit && (
                    <div className="flex items-center gap-2">
                        <label
                            htmlFor="pagination-row-limit"
                            className="text-sm text-neutral-dark dark:text-neutral-light opacity-80 whitespace-nowrap"
                        >
                            Rows per page
                        </label>
                        <div className="w-24">
                            <Select
                                id="pagination-row-limit"
                                value={String(currentLimit)}
                                onChange={handleLimitChange}
                                options={rowLimitOptions
                                    .filter((n) => n > 0)
                                    .sort((a, b) => a - b)
                                    .map((n) => ({ value: n, label: String(n) }))}
                            />
                        </div>
                    </div>
                )}
            </div>
            {totalPages > 1 && (
            <div className="flex max-w-full items-center gap-2 overflow-x-auto py-1">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                >
                    <ChevronsLeft size={18} />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                >
                    <ChevronLeft size={18} />
                </Button>

                {showPageNumbers &&
                    visiblePages.map((page, index) => {
                        if (page === "...") {
                            return (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="px-2 text-neutral-dark dark:text-neutral-light opacity-60"
                                >
                                    ...
                                </span>
                            );
                        }

                        const pageNum = page as number;
                        const isActive = pageNum === currentPage;

                        return (
                            <Button
                                key={pageNum}
                                variant={isActive ? "primary" : "ghost"}
                                size="sm"
                                onClick={() => onPageChange(pageNum)}
                                className={clsx(
                                    "min-w-[40px]",
                                    isActive && "font-semibold"
                                )}
                                aria-label={`Page ${pageNum}`}
                                aria-current={isActive ? "page" : undefined}
                            >
                                {pageNum}
                            </Button>
                        );
                    })}

                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                >
                    <ChevronRight size={18} />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                >
                    <ChevronsRight size={18} />
                </Button>
            </div>
            )}
        </div>
    );
};

export const PaginationBar = Pagination;
